import React, { useMemo } from "react";
import * as THREE from "three";
import { Text } from "@react-three/drei";

const GridResults3D = ({
    grid,
    results = [],
    gridScale = 1,
    showValues,
    minResult,
    type,
    camera
}) => {
    const cells = useMemo(() => {
        if (!grid?.origin || !Array.isArray(results)) return [];

        const { origin, xSpacing = 1, ySpacing = 1, edgeStart = false } = grid;

        const width = Number(origin.x.length);
        // For vertical grids, height is along Z instead of Y
        const height =
            type === "Vertical"
                ? Number(origin.z?.length ?? 0)
                : Number(origin.y?.length ?? 0);

        const actualCols = Math.floor(width / xSpacing);
        const actualRows = Math.floor(height / ySpacing);

        const cols = edgeStart ? actualCols + 1 : actualCols;
        const rows = edgeStart ? actualRows + 1 : actualRows;

        const adjustedCellWidth = edgeStart
            ? (width * gridScale) / (cols - 1)
            : (width * gridScale) / cols;

        const adjustedCellHeight = edgeStart
            ? (height * gridScale) / (rows - 1)
            : (height * gridScale) / rows;

        const startX = Number(origin.x.start) * gridScale;
        const startY = Number(origin.y.start) * gridScale * -1;
        const startZ = -Number(origin.z?.start ?? 0) * gridScale;

        // Overlay group is X-rotated in StageCanvas, so negative local Z renders above the DXF.
        const z = -0.11;

        const scaleX = grid.scaleX ?? 1;
        const scaleY = grid.scaleY ?? 1;
        const rotationDeg = grid.angle ?? 0;
        const rotationRad = -(rotationDeg * Math.PI) / 180;

        const items = [];

        // === HORIZONTAL GRID (XY plane) ===
        if (type === "Horizontal") {
            const anchor = new THREE.Vector2(startX, startY);

            for (let row = 0; row < rows; row++) {
                for (let col = 0; col < cols; col++) {
                    const idx = row * cols + col;
                    const value = results[idx] ?? null;

                    const offsetX = edgeStart ? 0 : adjustedCellWidth / 2;
                    const offsetY = edgeStart ? 0 : adjustedCellHeight / 2;

                    const rawX = startX + col * adjustedCellWidth + offsetX;
                    const rawY = startY - row * adjustedCellHeight - offsetY;

                    const p = new THREE.Vector2(rawX, rawY).sub(anchor);
                    p.x *= scaleX;
                    p.y *= scaleY;

                    const rotated = new THREE.Vector2(
                        p.x * Math.cos(rotationRad) - p.y * Math.sin(rotationRad),
                        p.x * Math.sin(rotationRad) + p.y * Math.cos(rotationRad)
                    ).add(anchor);

                    items.push({
                        position: [rotated.x, rotated.y, z],
                        value,
                    });
                }
            }
        }

        // === VERTICAL GRID (XZ plane) ===
        else if (type === "Vertical") {
            const anchor = new THREE.Vector2(startX, startY);
            const fixedZ = startZ;

            const dataCols = Math.floor(width / xSpacing);
            const dataRows = Math.floor(height / ySpacing);

            const cols = edgeStart ? dataCols + 1 : dataCols;
            const rows = edgeStart ? dataRows + 1 : dataRows;

            for (let row = 0; row < rows; row++) {
                for (let col = 0; col < cols; col++) {
                    const dataCol = Math.min(col, dataCols - 1);
                    const dataRow = Math.min(row, dataRows - 1);
                    const idx = dataRow * dataCols + dataCol;
                    const value = results[idx] ?? null;

                    const zPos = fixedZ - row * adjustedCellHeight - (edgeStart ? 0 : adjustedCellHeight / 2);
                    const offsetX = edgeStart ? 0 : adjustedCellWidth / 2;

                    const rawX = startX + col * adjustedCellWidth + offsetX;
                    const rawY = startY;

                    const p = new THREE.Vector2(rawX, rawY).sub(anchor);
                    p.x *= scaleX;
                    p.y *= scaleY;

                    const rotated = new THREE.Vector2(
                        p.x * Math.cos(rotationRad) - p.y * Math.sin(rotationRad),
                        p.x * Math.sin(rotationRad) + p.y * Math.cos(rotationRad)
                    ).add(anchor);

                    items.push({
                        position: [rotated.x, rotated.y, zPos],
                        value,
                        rotation: rotationRad,
                    });
                }
            }
        }


        return items;
    }, [grid, results, gridScale, type]);

    return (
        <>
            {grid?.showResults &&
                (() => {
                    const dataMax = Math.max(...cells.map((cell) => cell.value));
                    const dataMin = minResult || 1;

                    return cells.map((cell, i) => {
                        let color = "";
                        if (cell.value === dataMax) color = "darkgreen";
                        else if (cell.value < dataMin) color = "red";
                        else color = "cyan";

                        const opacity = cell.value != null ? 0.6 : 0;

                        const baseScale = type === "Vertical" ? [1, -1, 1] : [1, 1, 1]
                        const basePosition = type === "Vertical" ? [0, 0.1, 0] : [0, 0, -0.02];
                        const baseRotation =
                            type === "Vertical"
                                ? [Math.PI / 2, cell.rotation + Math.PI, 0]
                                : [Math.PI, 0, 0];

                        return (
                            <group key={i} position={cell.position}>
                                {/* Background or text depending on showValues */}
                                {!showValues ? (
                                    <mesh position={basePosition} rotation={baseRotation}>
                                        <planeGeometry args={[1 / grid.xSpacing, 1 / grid.ySpacing]} />
                                        <meshBasicMaterial color={color} transparent opacity={opacity} />
                                    </mesh>
                                ) : (
                                    cell.value != null && (
                                        <Text
                                            position={basePosition}
                                            rotation={baseRotation}
                                            scale={baseScale}
                                            fontSize={0.4 * gridScale}
                                            color={color}
                                            anchorX="center"
                                            anchorY="middle"
                                        >
                                            {Number(cell.value).toFixed(2)}
                                        </Text>
                                    )
                                )}
                            </group>
                        );
                    });
                })()}
        </>
    );
};

export default GridResults3D;
